import { useCallback } from 'react';
import { toast } from 'sonner';
import { hapticComplete, hapticTap } from '@/features/lifecycle/haptics';
import { useHabitStore } from '@/stores/habitStore';
import type { Habit, HabitCompletion } from '@/types';
import { toDateKey } from '@/lib/dateKey';
import { maybeCelebrateStreak } from './streakMilestones';

export interface UseHabitLog {
  /** Toggle today's completion for `habit`. Logs if empty, unlogs if done. */
  toggleToday: (habit: Habit) => void;
  /** Toggle a specific date for `habit`. Used by the 7-day grid for retro-log. */
  toggleDay: (habit: Habit, dateKey: string) => void;
}

export const useHabitLog = (): UseHabitLog => {
  const logCompletion = useHabitStore((s) => s.logCompletion);
  const logCompletionForDate = useHabitStore((s) => s.logCompletionForDate);
  const removeCompletion = useHabitStore((s) => s.removeCompletion);

  const log = useCallback(
    (habit: Habit, completion: HabitCompletion) => {
      void hapticComplete();
      toast.success(`Logged — ${habit.name}`, {
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: () => {
            removeCompletion(habit.id, completion.date);
          },
        },
      });
      // The store recomputed currentStreak as part of logging — read it fresh
      // so retro-logs that close a gap also count toward a milestone.
      const updated = useHabitStore.getState().habits.find((h) => h.id === habit.id);
      maybeCelebrateStreak(habit.name, habit.id, updated?.currentStreak ?? 0);
    },
    [removeCompletion],
  );

  const unlog = useCallback(
    (habit: Habit, dateKey: string) => {
      void hapticTap();
      toast(`Removed — ${habit.name}`, {
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: () => {
            logCompletionForDate(habit.id, dateKey);
          },
        },
      });
    },
    [logCompletionForDate],
  );

  const toggleToday = useCallback(
    (habit: Habit) => {
      const todayKey = toDateKey(new Date());
      const alreadyDone = useHabitStore
        .getState()
        .completions.some((c) => c.habitId === habit.id && c.date === todayKey);
      if (alreadyDone) {
        removeCompletion(habit.id, todayKey);
        unlog(habit, todayKey);
      } else {
        const completion = logCompletion(habit.id);
        if (completion) log(habit, completion);
      }
    },
    [log, logCompletion, removeCompletion, unlog],
  );

  const toggleDay = useCallback(
    (habit: Habit, dateKey: string) => {
      const alreadyDone = useHabitStore
        .getState()
        .completions.some((c) => c.habitId === habit.id && c.date === dateKey);
      if (alreadyDone) {
        removeCompletion(habit.id, dateKey);
        unlog(habit, dateKey);
      } else {
        const completion = logCompletionForDate(habit.id, dateKey);
        if (completion) log(habit, completion);
      }
    },
    [log, logCompletionForDate, removeCompletion, unlog],
  );

  return { toggleToday, toggleDay };
};
